/**
 * The entity a page is about, read from its URL.
 *
 * Every exploration, selection and reproduction page carries the entity's
 * id (and, for selections and reproductions, the kind) in its route. These
 * hooks read them once and hand the page the route builders for its own
 * steps, so no page keeps the open entity anywhere but in its URL.
 */
import { useLocation, useParams } from "react-router-dom";
import { explorationRoute } from "./explorationRoutes";
import {
    SELECTIONS_BASE, SELECTION_KINDS, selectionKindKeyForSlug, selectionRoute, selectionSetupRoute,
} from "./selectionFlow";
import {
    REPRODUCTIONS_BASE, REPRODUCTION_KINDS, reproductionListRoute, reproductionRoute,
} from "./reproductionFlow";

/** The kind segment of ``<base>/<kind>/...``. App.js declares one route per
 *  kind with the slug spelled out, so there is no ``:kind`` param to read;
 *  the segment comes from the pathname. A route that does declare the
 *  param (the tests' patterns) wins. */
function kindSlug(params, pathname, base) {
    if (params.kind) return params.kind;
    const rest = pathname.startsWith(`${base}/`) ? pathname.slice(base.length + 1) : "";
    return rest.split("/")[0] || undefined;
}

/** /colorexplorations/:explorationId/<step> */
export function useExplorationParams() {
    const { explorationId } = useParams();
    return {
        explorationId,
        stepRoute: (step) => explorationRoute(explorationId, step),
    };
}

/** /colorselections/:kind/(new|:selectionId)/<step>. ``selectionId`` is
 *  undefined on the create wizard. The kind is the URL's; a page that
 *  loads the selection may pass its stored type to ``stepRoute`` instead. */
export function useSelectionParams() {
    const params = useParams();
    const { selectionId } = params;
    const slug = kindSlug(params, useLocation().pathname, SELECTIONS_BASE);
    const kindKey = selectionKindKeyForSlug(slug);
    const kind = SELECTION_KINDS[kindKey];
    return {
        kindKey,
        kind,
        selectionType: kind.type,
        selectionId,
        listRoute: kind.listRoute,
        setupRoute: (step) => selectionSetupRoute(kind.type, step),
        stepRoute: (step, selectionType = kind.type) => selectionRoute(selectionType, selectionId, step),
    };
}

/** /colorreproductions/:kind/:reproductionId/<step> */
export function useReproductionParams() {
    const params = useParams();
    const { reproductionId } = params;
    const slug = kindSlug(params, useLocation().pathname, REPRODUCTIONS_BASE);
    const kind = REPRODUCTION_KINDS[slug] || REPRODUCTION_KINDS.logo;
    return {
        kind,
        reproductionType: kind.type,
        reproductionId,
        listRoute: reproductionListRoute(kind.type),
        stepRoute: (step, reproductionType = kind.type) => reproductionRoute(reproductionType, reproductionId, step),
    };
}
